import React, {useEffect, useState} from 'react';
import TableComponent from '@/components/table/Table';
import axios from 'axios';
import CellLoader from '@/components/loading/CellLoader';
import {buildURL} from '@/utils/urlBuilder';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';

interface UserAccessLog {
    createdOn: string;
    username: string;
}

interface UserAccessLogsResponse {
    userActivity: UserAccessLog[];
    pagesList: number[];
}

interface UserAccessLogsTableProps {
    eventId: number;
    shouldFetchLogs: boolean;
}

const UserAccessLogsTable: React.FC<UserAccessLogsTableProps> = React.memo(({eventId, shouldFetchLogs}) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [userAccessLogs, setUserAccessLogs] = useState<UserAccessLog[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pagesList, setPagesList] = useState<number[]>([]);

    const getUserAccessLogs = (page: number): Promise<{data: UserAccessLogsResponse}> => {
        const url = buildURL('userAccessLogs', {obj_id: eventId});
        return axios.get(url, {params: {page}});
    };

    useEffect(() => {
        if (!shouldFetchLogs) {return;}

        const fetchData = async (): Promise<void> => {
            setLoading(true);
            try {
                const response = await getUserAccessLogs(currentPage);
                setUserAccessLogs(response.data.userActivity || []);
                setPagesList(response.data.pagesList || []);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err instanceof Error ? err.message : 'Unknown error');
                setLoading(false);
            }
        };

        fetchData();
    }, [eventId, currentPage, shouldFetchLogs]);

    if (loading) {
        return <CellLoader fullHeight={true} />;
    }

    if (userAccessLogs.length === 0) {
        return (
            <span className="m-3">
                {i18n.t('jt_data_available.text')}
            </span>
        );
    }

    const columns = [
        {
            header: i18n.t('th_date_accessed.text'),
            accessorKey: 'createdOn',
            cell: (cell: {row: {original: UserAccessLog}}) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_username.text'),
            accessorKey: 'username',
        },
    ];

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
            <div className="card">
                <div className="card-body">
                    <TableComponent data={userAccessLogs} columns={columns} customTable={true} />
                    <PaginationComponent
                        pages={pagesList}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
    );
});

export default UserAccessLogsTable; 